import React, { useState, useEffect } from 'react';
import {
  Box, Stack, Typography, Button,
  Paper, Grid, CircularProgress, Chip,
  Card, CardContent, Dialog, useMediaQuery, useTheme
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Task as TaskIcon,
  Phone as PhoneIcon,
  AccountBox as AccountBoxIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  ReplySharp as ReplySharpIcon,
  AutoMode as AutoModeIcon
} from '@mui/icons-material';

import { green, blue, orange, red } from '@mui/material/colors';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

// Importar controladores e utilitários
import { getLeadTasks } from '../../../controllers/crm/leadTaskController';
import { encerrarLead } from '../../../controllers/crm/leadController';
import { taskStatus } from '../../../constants/crm';
import { handleSendWebWhatsapp } from '../../../utils/crm/openWhatsApp';

// Importar componentes relacionados
import LeadTaskDetails from './LeadTaskDetails';
import ClienteDetalhe from '../Clientes/ClienteDetalhe';
import LeadEncerramento from './LeadEncerramento';
import TransferirLead from '../Corretores/TransferirLead';

dayjs.locale('pt-br');

export default function LeadControleDetail({
  account,
  lead,
  setShowLeadControle,
  setSnackbar,
  setUpdate,
  update
}) {
  // Hook para detectar telas pequenas
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Estados para carregamento e controle
  const [loading, setLoading] = useState(false);

  // Estados para dados do lead (apenas para visualização)
  const leadData = {
    nome: lead?.nome || '',
    email: lead?.email || '',
    telefone: lead?.telefone || '',
    interesse: lead?.interesse || '',
    faixaderenda: lead?.faixaderenda || '',
    dtdesignacao: lead?.dtdesignacao || ''
  };

  // Estados para tarefas
  const [taskList, setTaskList] = useState([]);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [selectedTask, setSelectedTask] = useState({});

  // Estado para controlar ClienteDetalhe
  const [showClienteDetalhe, setShowClienteDetalhe] = useState(false);

  // Estados para encerramento de leads
  const [showEncerrarLead, setShowEncerrarLead] = useState(false);
  const [motivoEncerramento, setMotivoEncerramento] = useState('');
  const [observacaoEncerramento, setObservacaoEncerramento] = useState('');
  const [loadingEncerrar, setLoadingEncerrar] = useState(false);

  // Estados para transferência de lead
  const [showTransferirLead, setShowTransferirLead] = useState(false);

  // Carregar tarefas do lead
  const loadTasks = async () => {
    if (!lead?.idlead) return;

    setLoading(true);
    try {
      const query = {
        where: {
          idlead: lead.idlead,
          idoperador: account.id,
          isactive: true,
        },
        orderBy: [{ dtacao: 'desc' }]
      };

      const response = await getLeadTasks(query);
      if (response.status === 200) {
        setTaskList(response.data);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao carregar tarefas: ' + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Criar nova tarefa
  const handleCreateTask = () => {
    setSelectedTask({});
    setShowTaskDetail(true);
  };

  // Abrir tarefa existente
  const handleOpenTask = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  // Fechar componente
  const handleClose = () => {
    setShowLeadControle(false);
  };

  // Abrir ClienteDetalhe
  const handleOpenClienteDetalhe = () => {
    setShowClienteDetalhe(true);
  };

  // Funções para encerramento de leads
  const handleOpenEncerrarLead = () => {
    setShowEncerrarLead(true);
    setMotivoEncerramento('');
    setObservacaoEncerramento('');
  };

  const handleCloseEncerrarLead = () => {
    setShowEncerrarLead(false);
    setMotivoEncerramento('');
    setObservacaoEncerramento('');
  };

  const handleEncerrarLead = async () => {
    if (!lead || !motivoEncerramento) {
      setSnackbar({
        open: true,
        message: 'Selecione um motivo para o encerramento',
        severity: 'warning'
      });
      return;
    }

    try {
      setLoadingEncerrar(true);

      const encerramentoData = {
        idlead: lead.idlead,
        motivo: motivoEncerramento,
        observacao: observacaoEncerramento,
        idoperador: account.id,
        idconta: account.conta,
        version: lead.version
      };

      const response = await encerrarLead(encerramentoData);

      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: 'Lead encerrado com sucesso',
          severity: 'success'
        });

        setUpdate && setUpdate(Date.now());
        handleCloseEncerrarLead();

        // Fechar o modal de detalhes após encerrar o lead
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        throw new Error('Erro na resposta do servidor');
      }
    } catch (error) {
      console.error('Erro ao encerrar lead:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao encerrar lead. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setLoadingEncerrar(false);
    }
  };

  // Funções para transferência de lead
  const handleOpenTransferirLead = () => {
    setShowTransferirLead(true);
  };

  const handleCloseTransferirLead = () => {
    setShowTransferirLead(false);
  };

  const handleTransferenciaSuccess = (leadAtualizado) => {
    setSnackbar({
      open: true,
      message: `Lead transferido com sucesso para ${leadAtualizado.corretor}`,
      severity: 'success'
    });

    // Atualizar a lista e fechar o modal
    setUpdate && setUpdate(Date.now());
    
    // Fechar o modal de detalhes após transferir o lead
    setTimeout(() => {
      handleClose();
    }, 1000);
  };

  // Carregar tarefas ao montar o componente
  useEffect(() => {
    if (lead?.idlead) {
      loadTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.idlead, update]);

  // Componente para exibir informações do lead (apenas visualização)
  const InfoItem = ({ label, value, icon }) => (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0 }}>
        {icon}
        <Typography variant="h5" color="text.secondary" fontWeight="medium">
          {label}
        </Typography>
      </Stack>
      <Typography variant="body2" fontWeight="medium" sx={{ pl: 3 }}>
        {value || 'Não informado'}
      </Typography>
    </Box>
  );

  // Componente para exibir tarefa
  const TaskItem = ({ task, index }) => (
    <Paper
      key={`${task.idacao}-${index}`}
      elevation={1}
      onClick={() => handleOpenTask(task)}
      sx={{
        p: 2,
        mb: 1,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        },
        borderLeft: `4px solid ${task.situacao === taskStatus.Description.co ? green[500] : orange[500]}`
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="medium">
          {task.titulo}
        </Typography>
        <Chip
          label={dayjs(task.dtacao).format('DD/MM/YY')}
          size="small"
          sx={{
            bgcolor: task.situacao === taskStatus.Description.co ? green[100] : orange[100],
            color: task.situacao === taskStatus.Description.co ? green[800] : orange[800]
          }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {task.descricao}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {dayjs(task.dtacao).format('DD/MM/YYYY HH:mm')}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999
        }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Carregando...
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Verificar se o lead existe antes de renderizar */}
      {!lead ? (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}>
          <Typography variant="h6" color="text.secondary">
            Nenhum lead selecionado
          </Typography>
        </Box>
      ) : (
        <>
          {/* Header */}
          <Card elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', borderRadius: 0 }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" color="primary" fontWeight="bold">
                  Controle do Lead
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                >
                  Fechar
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Stack spacing={2}>
              {/* Dados do Lead */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card elevation={2}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="h4" fontWeight="bold">
                        Informações do Lead
                      </Typography>
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <InfoItem
                          label="Nome Completo"
                          value={leadData.nome}
                          icon={<PersonIcon fontSize="small" sx={{ color: blue[600] }} />}
                        />

                        <InfoItem
                          label="Telefone"
                          value={leadData.telefone}
                          icon={<PhoneIcon fontSize="small" sx={{ color: green[600] }} />}
                        />

                        <InfoItem
                          label="E-mail"
                          value={leadData.email}
                          icon={<EmailIcon fontSize="small" sx={{ color: orange[600] }} />}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <InfoItem
                          label="Interesse"
                          value={leadData.interesse}
                          icon={<InfoIcon fontSize="small" sx={{ color: blue[600] }} />}
                        />

                        <InfoItem
                          label="Designado em"
                          value={dayjs(leadData.dtdesignacao).format('DD/MM/YYYY HH:mm')}
                          icon={<AutoModeIcon fontSize="small" sx={{ color: orange[600] }} />}
                        />

                        {lead.fbtraceid && (
                          <InfoItem
                            label="Tracker ID"
                            value={lead.fbtraceid}
                            icon={<InfoIcon fontSize="small" sx={{ color: blue[600] }} />}
                          />
                        )}
                      </Grid>

                      {/* Ações rápidas */}
                      <Grid item xs={12}>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CloseIcon />}
                            onClick={handleOpenEncerrarLead}
                            sx={{ color: red[600], borderColor: red[600] }}
                          >
                            Encerrar
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ReplySharpIcon />}
                            onClick={handleOpenTransferirLead}
                            sx={{ color: blue[600], borderColor: blue[600] }}
                          >
                            Transferir
                          </Button>                          
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AccountBoxIcon />}
                            onClick={handleOpenClienteDetalhe}
                            sx={{ color: blue[600], borderColor: blue[600] }}
                          >
                            Cliente
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<WhatsAppIcon />}
                            onClick={() => handleSendWebWhatsapp(leadData.telefone, account.nome, 'Olá! Como posso ajudá-lo?')}
                            sx={{ color: green[600], borderColor: green[600] }}
                          >
                            WhatsApp
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Tarefas */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Card elevation={2}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TaskIcon color="primary" />
                        <Typography variant="h4" fontWeight="bold">
                          Tarefas
                        </Typography>
                      </Stack>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleCreateTask}
                      >
                        Nova Tarefa
                      </Button>
                    </Stack>

                    <Box sx={{
                      height: '300px',
                      minHeight: '300px',
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                      }
                    }}>
                      {taskList.length === 0 ? (
                        <Box sx={{
                          textAlign: 'center',
                          py: 4,
                          color: 'text.secondary'
                        }}>
                          <TaskIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                          <Typography variant="body2">
                            Nenhuma tarefa encontrada
                          </Typography>
                        </Box>
                      ) : (
                        taskList.map((task, index) => (
                          <TaskItem key={`task-${task.idacao}-${index}`} task={task} index={index} />
                        ))
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Stack>
          </Box>

          {/* Dialog para detalhes da tarefa */}
          {showTaskDetail && (
            <LeadTaskDetails
              account={account}
              lead={lead}
              task={selectedTask}
              taskList={taskList}
              taskListFiltered={taskList}
              filtro={null}
              setShowTask={setShowTaskDetail}
              showTask={showTaskDetail}
              setSnackbar={setSnackbar}
              setUpdate={setUpdate}
              update={update}
            />
          )}

          {/* Dialog para ClienteDetalhe */}
          <Dialog
            open={showClienteDetalhe}
            onClose={() => setShowClienteDetalhe(false)}
            fullScreen={isSmallScreen}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                height: isSmallScreen ? '100vh' : 'auto',
                maxHeight: isSmallScreen ? '100vh' : '90vh',
                margin: isSmallScreen ? 0 : 2,
                borderRadius: isSmallScreen ? 0 : 2,
              }
            }}
          >
            {showClienteDetalhe && (
              <ClienteDetalhe
                account={account}
                lead={lead}
                showClient={showClienteDetalhe}
                setShowClient={setShowClienteDetalhe}
                setShowTaskList={() => { }} // Função vazia pois não usamos
                setSnackbar={setSnackbar}
                setShowInfLead={() => { }} // Função vazia pois não usamos
                setShowMyLeads={() => { }} // Função vazia pois não usamos
                setShowLeadDetails={() => { }} // Função vazia pois não usamos
                setUpdate={setUpdate}
              />
            )}
          </Dialog>

          {/* Modal de Encerramento de Lead */}
          <LeadEncerramento
            lead={lead}
            showEncerrarLead={showEncerrarLead}
            handleCloseEncerrarLead={handleCloseEncerrarLead}
            handleEncerrarLead={handleEncerrarLead}
            motivoEncerramento={motivoEncerramento}
            setMotivoEncerramento={setMotivoEncerramento}
            observacaoEncerramento={observacaoEncerramento}
            setObservacaoEncerramento={setObservacaoEncerramento}
            loadingEncerrar={loadingEncerrar}
          />

          {/* Modal de Transferência de Lead */}
          <TransferirLead
            open={showTransferirLead}
            onClose={handleCloseTransferirLead}
            lead={lead}
            idconta={account.conta}
            onSuccess={handleTransferenciaSuccess}
            setSnackbar={setSnackbar}
          />
        </>
      )}
    </Box>
  );
}